import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CounterpartySidebar from './CounterpartySidebar';
import CounterpartyHeader from './CounterpartyHeader';
import { VndFullscreenLoading } from './VndPageLoading';
import { TransactionsFilterProvider } from '../context/TransactionsFilterContext';
import { DashboardFilterProvider } from '../context/DashboardFilterContext';
import { cpApi } from '../api/api';
import { VndRequestsFiltersContext } from '../context/VndRequestsFiltersContext';
import type { Counterparty, CounterpartyTeam } from '../types';

interface Props {
  children: React.ReactNode;
}

export default function CounterpartyLayout({ children }: Props) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<Counterparty | null>(null);
  const [team, setTeam] = useState<CounterpartyTeam | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isVndRequestsPage = location.pathname === '/requests';
  const isVndPayoutsPage = location.pathname === '/payouts';
  const isTeamPage = location.pathname === '/team';
  const isTransactionsPage = location.pathname === '/transactions';
  const isDashboardPage = location.pathname === '/dashboard';
  const isQrProfilesPage = location.pathname === '/qr-profiles';
  const isSmsPhonesPage = location.pathname === '/sms-phones';

  const [statusFilter, setStatusFilter] = useState('all');
  const [profileId, setProfileId] = useState('all');
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return { from: yesterday, to: today };
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Ставим дефолт только когда оба пусты — иначе перезаписываем промежуточный выбор (from выбран, to ещё нет)
    if (isVndRequestsPage && !dateRange.from && !dateRange.to) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      setDateRange({ from: yesterday, to: today });
    }
  }, [isVndRequestsPage, dateRange.from, dateRange.to]);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('airex_cp_token');
        if (!token) {
          navigate('/login');
          return;
        }
        const [profile, myTeam] = await Promise.all([
          cpApi.getProfile(),
          cpApi.getVndTeam().catch(() => null),
        ]);
        setUser(profile);
        setTeam(myTeam ? {
          id: myTeam.team.id,
          name: myTeam.team.name,
          responsible_counterparty_id: myTeam.team.responsible_counterparty_id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          balance: myTeam.members.reduce((sum, m) => sum + (m.salary_balance || 0), 0),
          members: myTeam.members.map((m) => ({
            id: m.counterparty_id,
            team_id: myTeam.team.id,
            counterparty_id: m.counterparty_id,
            is_responsible: m.counterparty_id === myTeam.team.responsible_counterparty_id,
            can_cancel: false,
            created_at: new Date().toISOString(),
            counterparty_name: m.name,
            login: m.login,
            salary_balance: m.salary_balance,
          })),
        } : null);
      } catch {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  useEffect(() => {
    const onProfileRefresh = () => {
      const token = localStorage.getItem('airex_cp_token');
      if (!token) return;
      void cpApi
        .getProfile()
        .then(setUser)
        .catch(() => navigate('/login'));
    };
    window.addEventListener('vndCpProfileRefresh', onProfileRefresh);
    return () => window.removeEventListener('vndCpProfileRefresh', onProfileRefresh);
  }, [navigate]);

  const isResponsible = !!(team && user && team.responsible_counterparty_id === user.id);
  const teamBalance = team?.balance ?? 0;
  const myBalance = teamBalance;

  useEffect(() => {
    // На мобиле после навигации закрываем drawer, чтобы контент не перекрывался.
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && user && !user.twofa_check && location.pathname !== '/settings') {
      navigate('/settings', { replace: true });
    }
  }, [loading, location.pathname, navigate, user]);

  useEffect(() => {
    if (user && !user.twofa_check) return;
    if (isResponsible) return;
    if (
      location.pathname === '/dashboard' ||
      location.pathname === '/team' ||
      location.pathname === '/transactions'
    ) {
      navigate('/qr-profiles', { replace: true });
    }
    if (location.pathname === '/payouts' && !user?.can_process_payouts) {
      navigate('/qr-profiles', { replace: true });
    }
  }, [isResponsible, location.pathname, navigate, user, user?.can_process_payouts]);

  const handleStatusFilterChange = (status: string) => setStatusFilter(status);
  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => setDateRange(range);
  const handleSearchChange = (query: string) => setSearchQuery(query);

  const vndRequestsFiltersValue = useMemo(
    () => ({ statusFilter, dateRange, searchQuery, profileId }),
    [statusFilter, dateRange, searchQuery, profileId]
  );

  if (loading) {
    return <VndFullscreenLoading />;
  }

  if (!user) return null;

  return (
    <div className="h-dvh min-h-0 flex overflow-hidden bg-gray-50 dark:bg-gray-950">
      <CounterpartySidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
        user={user}
        team={team}
        teamBalance={teamBalance}
        myBalance={myBalance}
        isResponsible={isResponsible}
      />
      <TransactionsFilterProvider team={team} isResponsible={isResponsible} user={user}>
      <DashboardFilterProvider>
      <VndRequestsFiltersContext.Provider value={isVndRequestsPage || isVndPayoutsPage ? vndRequestsFiltersValue : null}>
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-gray-50 dark:bg-gray-950">
        <CounterpartyHeader
          onMenuToggle={() => setSidebarOpen((o) => !o)}
          isRequestsPage={isVndRequestsPage}
          isSettingsPage={location.pathname === '/settings'}
          showCreateQrButton={isQrProfilesPage && isResponsible}
          showCreateSmsPhoneButton={isSmsPhonesPage}
          showVndQrProfilesFilters={isQrProfilesPage}
          showVndRequestsFilters={isVndRequestsPage || isVndPayoutsPage}
          isPayoutsPage={isVndPayoutsPage}
          showAirexRequestsFilters={false}
          showTeamSearch={isTeamPage}
          showTransactionsFilters={isTransactionsPage}
          showDashboardDateFilter={isDashboardPage}
          teamSearchQuery={teamSearchQuery}
          onTeamSearchChange={(q) => {
            setTeamSearchQuery(q);
            window.dispatchEvent(new CustomEvent('teamSearchChange', { detail: { search: q } }));
          }}
          showAddTeamMemberButton={isTeamPage && !!team && !!user && team.responsible_counterparty_id === user.id}
          showNewTransactionButton={false}
          onNewTransaction={() => window.dispatchEvent(new CustomEvent('openTransferWidget'))}
          showWithdrawButton={false}
          onWithdraw={() => undefined}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          profileId={profileId}
          onProfileIdChange={setProfileId}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onRefresh={() => window.dispatchEvent(new CustomEvent('refreshRequests'))}
        />
        <main className="relative z-0 flex-1 overflow-y-auto w-full min-w-0 min-h-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:pb-0">{children}</main>
      </div>
      </VndRequestsFiltersContext.Provider>
      </DashboardFilterProvider>
      </TransactionsFilterProvider>
    </div>
  );
}
